import React, { useEffect, useMemo, useState } from "react";
import { IconUser } from "@tabler/icons-react";
import "./media.css";

interface EventDto {
    eventID?: number;
    eventId?: number;
    eventName?: string;
    name?: string;
}

interface FunctionDto {
    functionId: number;
    functionName: string;
}

interface MediaApplication {
    id: number;
    userId: number;
    personId?: number | null;
    firstname: string;
    lastname: string;
    email: string;
    functionId: number;
    status: "Pending" | "Approved" | "Denied";
    note: string;
    createdAt?: string;
    personImagePath?: string | null;
}

const MediaAdminPage: React.FC = () => {
    const apiUrl = process.env.REACT_APP_API_URL as string;

    const [events, setEvents] = useState<EventDto[]>([]);
    const [selectedEvent, setSelectedEvent] = useState<number | null>(null);
    const [applications, setApplications] = useState<MediaApplication[]>([]);
    const [functions, setFunctions] = useState<FunctionDto[]>([]);
    const [filter, setFilter] =
        useState<"All" | "Pending" | "Approved" | "Denied">("All");

    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    useEffect(() => {
        fetch(`${apiUrl}/api/events`)
            .then(res => res.json())
            .then(setEvents);
    }, [apiUrl]);

    const load = async (eventId: number) => {
        const apps = await fetch(
            `${apiUrl}/api/media-applications/event/${eventId}`
        );

        setApplications(apps.ok ? await apps.json() : []);

        const funcs = await fetch(
            `${apiUrl}/api/events/${eventId}/media-data`
        );

        const f = funcs.ok
            ? await funcs.json()
            : { functions: [] };

        setFunctions(f.functions || []);
    };

    useEffect(() => {
        if (!selectedEvent) {
            setApplications([]);
            setFunctions([]);
            return;
        }

        load(selectedEvent);
    }, [selectedEvent]);

    const updateStatus = async (
        id: number,
        status: "Approved" | "Denied"
    ) => {
        await fetch(
            `${apiUrl}/api/media-applications/${id}/status?status=${status}`,
            {
                method: "PUT"
            }
        );

        if (selectedEvent) {
            await load(selectedEvent);
        }
    };

    const stats = useMemo(() => ({
        total: applications.length,
        pending: applications.filter(a => a.status === "Pending").length,
        approved: applications.filter(a => a.status === "Approved").length,
        denied: applications.filter(a => a.status === "Denied").length,
    }), [applications]);

    const filteredApplications = applications.filter(a => {
        if (filter === "All") return true;
        return a.status === filter;
    });

    const getFunctionName = (functionId: number) =>
        functions.find(f => f.functionId === functionId)?.functionName || "Ukendt";

    const getImageUrl = (path?: string | null) => {
        if (!path) return null;

        return `${apiUrl}${path.startsWith("/") ? "" : "/"}${path}`;
    };

    return (
        <div className="media-admin-container">
            <div className="media-admin-header">
                <div>
                    <h1>Media Applications</h1>
                    <p>Administrer medieadgange</p>
                </div>

                <select
                    className="media-event-select"
                    value={selectedEvent ?? ""}
                    onChange={(e) =>
                        setSelectedEvent(
                            e.target.value
                                ? Number(e.target.value)
                                : null
                        )
                    }
                >
                    <option value="">Vælg event</option>

                    {events.map(ev => {
                        const id = ev.eventID ?? ev.eventId;

                        return (
                            <option key={id} value={id}>
                                {ev.eventName ?? ev.name}
                            </option>
                        );
                    })}
                </select>
            </div>

            <div className="media-filters">
                <button
                    className={filter === "All" ? "active" : ""}
                    onClick={() => setFilter("All")}
                >
                    All ({stats.total})
                </button>

                <button
                    className={`${filter === "Pending" ? "active " : ""}pending`}
                    onClick={() => setFilter("Pending")}
                >
                    Pending ({stats.pending})
                </button>

                <button
                    className={`${filter === "Approved" ? "active " : ""}approved`}
                    onClick={() => setFilter("Approved")}
                >
                    Approved ({stats.approved})
                </button>

                <button
                    className={`${filter === "Denied" ? "active " : ""}denied`}
                    onClick={() => setFilter("Denied")}
                >
                    Denied ({stats.denied})
                </button>
            </div>

            {!selectedEvent && (
                <div className="media-empty">
                    Vælg et event for at se ansøgninger.
                </div>
            )}

            {selectedEvent && filteredApplications.length === 0 && (
                <div className="media-empty">
                    Ingen ansøgninger matcher dette filter.
                </div>
            )}

            {selectedEvent && filteredApplications.length > 0 && (
                <div className="media-app-card-list">
                    {filteredApplications.map(app => {
                        const imageUrl = getImageUrl(app.personImagePath);

                        return (
                            <div
                                key={app.id}
                                className={`media-admin-card ${app.status}`}
                            >
                                <div className="media-admin-card-top">
                                    <div className="media-person-header">
                                        <div className="media-person-image-wrapper">
                                            {imageUrl ? (
                                                <button
                                                    type="button"
                                                    className="media-person-image-button"
                                                    onClick={() =>
                                                        setSelectedImage(imageUrl)
                                                    }
                                                    title="Klik for at se billedet"
                                                >
                                                    <img
                                                        src={imageUrl}
                                                        alt={`${app.firstname} ${app.lastname}`}
                                                        className="media-person-image"
                                                    />
                                                </button>
                                            ) : (
                                                <div
                                                    className="media-person-image-placeholder"
                                                    title="Intet billede"
                                                >
                                                    <IconUser
                                                        size={32}
                                                        stroke={1.5}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <div>
                                            <h3>
                                                {app.firstname} {app.lastname}
                                            </h3>
                                            <p>{app.email}</p>
                                        </div>
                                    </div>

                                    <span className={`media-status ${app.status}`}>
                                        {app.status}
                                    </span>
                                </div>

                                <div className="media-admin-card-info">
                                    <div>
                                        <span>Funktion</span>
                                        <strong>
                                            {getFunctionName(app.functionId)}
                                        </strong>
                                    </div>

                                    <div>
                                        <span>Application ID</span>
                                        <strong>#{app.id}</strong>
                                    </div>

                                    <div>
                                        <span>Oprettet</span>
                                        <strong>
                                            {app.createdAt
                                                ? new Date(app.createdAt).toLocaleString("da-DK")
                                                : "-"}
                                        </strong>
                                    </div>

                                    <div className="note-info-box">
                                        <span>Note</span>
                                        <strong>
                                            {app.note || "Ingen note"}
                                        </strong>
                                    </div>
                                </div>

                                {app.status === "Pending" && (
                                    <div className="media-actions">
                                        <button
                                            className="approve-btn"
                                            onClick={() =>
                                                updateStatus(app.id, "Approved")
                                            }
                                        >
                                            Approve
                                        </button>

                                        <button
                                            className="deny-btn"
                                            onClick={() =>
                                                updateStatus(app.id, "Denied")
                                            }
                                        >
                                            Deny
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {selectedImage && (
                <div
                    className="media-image-modal"
                    onClick={() => setSelectedImage(null)}
                >
                    <div
                        className="media-image-modal-content"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            className="media-image-modal-close"
                            onClick={() => setSelectedImage(null)}
                        >
                            ×
                        </button>

                        <img
                            src={selectedImage}
                            alt="Person"
                            className="media-image-modal-full"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MediaAdminPage;